/**
 * 클라이언트 사전 이미지 압축 유틸리티
 * 모바일 카메라 사진(3-12MB, 3000-4000px)을 업로드 전에 리사이즈 + JPEG 압축
 * 서버(API)에서 추가로 height 500px, quality 85로 후처리함
 */

import { createLogger } from "@ict/shared";

const logger = createLogger({ module: "ImageCompress" });

/** 불필요한 압축을 건너뛰는 파일 크기 임계값 (1MB) */
const SKIP_THRESHOLD_BYTES = 1 * 1024 * 1024;

export interface CompressOptions {
  /** 최대 너비 (기본 2000) */
  maxWidth?: number;
  /** 최대 높이 (기본 2000) */
  maxHeight?: number;
  /** JPEG 압축 품질 0~1 (기본 0.8) */
  quality?: number;
  /** 출력 MIME 타입 (기본 'image/jpeg') */
  mimeType?: string;
}

const DEFAULT_OPTIONS: Required<CompressOptions> = {
  maxWidth: 2000,
  maxHeight: 2000,
  quality: 0.8,
  mimeType: "image/jpeg",
};

/**
 * 이미지를 리사이즈 + JPEG 압축
 * - createImageBitmap 우선 사용, 미지원 시 Image 엘리먼트 폴백
 * - OffscreenCanvas 사용 가능하면 사용, 아니면 일반 Canvas
 * - 원본이 maxWidth/maxHeight 이내이고 1MB 이하면 원본 반환
 * - 에러 시 원본 File 반환 (fallback safety)
 */
export async function compressImage(file: File, options?: CompressOptions): Promise<File> {
  const opts = { ...DEFAULT_OPTIONS, ...options };

  try {
    // 이미지 소스 로드
    const source = await loadImageSource(file);
    const { width: origW, height: origH } = source;

    // 리사이즈 불필요 + 파일 크기 작으면 원본 반환
    if (origW <= opts.maxWidth && origH <= opts.maxHeight && file.size <= SKIP_THRESHOLD_BYTES) {
      logger.debug(`압축 생략: ${origW}x${origH}, ${formatBytes(file.size)}`);
      disposeSource(source);
      return file;
    }

    // 비율 유지 리사이즈 크기 계산
    const { width, height } = calcResizedDimensions(origW, origH, opts.maxWidth, opts.maxHeight);

    // Canvas에 그리기 + Blob 변환
    const blob = await drawAndCompress(source, width, height, opts);
    disposeSource(source);

    // Blob → File 변환
    const ext = opts.mimeType === "image/png" ? ".png" : ".jpg";
    const compressedName = replaceExtension(file.name, ext);
    const compressed = new File([blob], compressedName, {
      type: opts.mimeType,
      lastModified: Date.now(),
    });

    logger.debug(
      `압축 완료: ${origW}x${origH} → ${width}x${height}, ` +
        `${formatBytes(file.size)} → ${formatBytes(compressed.size)}`
    );

    return compressed;
  } catch (error) {
    // 에러 시 원본 반환 (fallback safety)
    logger.warn("이미지 압축 실패, 원본 사용:", error);
    return file;
  }
}

/** 이미지 소스 (ImageBitmap 또는 HTMLImageElement) */
type ImageSource = (ImageBitmap | HTMLImageElement) & {
  width: number;
  height: number;
};

/** createImageBitmap 우선 사용, 미지원 시 Image 엘리먼트 폴백 */
async function loadImageSource(file: File): Promise<ImageSource> {
  if (typeof createImageBitmap === "function") {
    return createImageBitmap(file) as Promise<ImageSource>;
  }

  // 폴백: HTMLImageElement + ObjectURL
  return new Promise<ImageSource>((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img as ImageSource);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("이미지 로드 실패"));
    };
    img.src = url;
  });
}

/** ImageBitmap은 명시적 close 필요 */
function disposeSource(source: ImageSource): void {
  if ("close" in source && typeof source.close === "function") {
    source.close();
  }
}

/** 비율 유지하며 최대 크기 내로 축소 */
function calcResizedDimensions(
  origW: number,
  origH: number,
  maxW: number,
  maxH: number
): { width: number; height: number } {
  if (origW <= maxW && origH <= maxH) {
    return { width: origW, height: origH };
  }

  const ratio = Math.min(maxW / origW, maxH / origH);
  return {
    width: Math.round(origW * ratio),
    height: Math.round(origH * ratio),
  };
}

/** Canvas에 이미지 그리고 Blob으로 압축 */
async function drawAndCompress(
  source: ImageSource,
  width: number,
  height: number,
  opts: Required<CompressOptions>
): Promise<Blob> {
  // OffscreenCanvas 사용 가능하면 사용 (메인 스레드 블로킹 감소)
  if (typeof OffscreenCanvas !== "undefined") {
    const canvas = new OffscreenCanvas(width, height);
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("OffscreenCanvas 2d context 생성 실패");
    ctx.drawImage(source, 0, 0, width, height);
    return canvas.convertToBlob({ type: opts.mimeType, quality: opts.quality });
  }

  // 폴백: 일반 Canvas + toBlob
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2d context 생성 실패");
  ctx.drawImage(source, 0, 0, width, height);

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error("Canvas toBlob 실패"));
        }
      },
      opts.mimeType,
      opts.quality
    );
  });
}

/** 파일명 확장자 교체 */
function replaceExtension(filename: string, newExt: string): string {
  const dotIndex = filename.lastIndexOf(".");
  if (dotIndex === -1) return filename + newExt;
  return filename.substring(0, dotIndex) + newExt;
}

/** 바이트 수를 읽기 쉬운 단위로 변환 */
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}
